import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { getWarrantyStatus, getWarrantyColor } from '../../warrantyUtils';
import { LOOPS } from './supportFlowData';

const API_BASE = process.env.REACT_APP_API_URL;

// Statuses that mean a case is still live — a unit already in one of these
// should almost never get a second RMA.
const OPEN_STATUSES = new Set([
  'pending', 'assigned', 'received', 'awaiting_repair', 'repairing', 'awaiting_shipping', 'shipped',
]);

function splitTags(raw) {
  return Array.from(new Set(
    raw.split(/[\s,;]+/).map(t => t.trim()).filter(Boolean)
  )).slice(0, 25);
}

export default function SupportStartRmaPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const resolve = useCallback(async () => {
    const tags = splitTags(raw);
    if (tags.length === 0) {
      setError('Enter at least one service tag.');
      return;
    }
    setError('');
    setLoading(true);
    setResults(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const found = await Promise.all(tags.map(async tag => {
        try {
          // The authenticated device search — the public service-tag endpoint is
          // rate-limited to one lookup per 10 s, so it cannot resolve a list.
          const res = await fetch(
            `${API_BASE}/devices?search=${encodeURIComponent(tag)}&limit=10`,
            { headers }
          );
          if (!res.ok) return { tag, state: 'error', message: `Lookup failed (${res.status})` };
          const data = await res.json();
          const list = Array.isArray(data.devices) ? data.devices : [];
          const exact = list.find(d =>
            String(d.serviceTagNumber || '').toLowerCase() === tag.toLowerCase());
          if (!exact) return { tag, state: 'missing' };

          let rmas = [];
          if (exact.serialNumber) {
            try {
              const r = await fetch(
                `${API_BASE}/rma/${encodeURIComponent(exact.serialNumber)}`,
                { headers }
              );
              if (r.ok) {
                const payload = await r.json();
                rmas = Array.isArray(payload) ? payload : [];
              }
            } catch (err) {
              rmas = [];
            }
          }
          return { tag, state: 'found', device: exact, rmas };
        } catch (err) {
          return { tag, state: 'error', message: 'Network error' };
        }
      }));

      setResults(found);
    } catch (err) {
      setError('Could not reach the device registry. Try again.');
    } finally {
      setLoading(false);
    }
  }, [raw, getToken]);

  const onKeyDown = e => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      resolve();
    }
  };

  const foundCount = results ? results.filter(r => r.state === 'found').length : 0;

  return (
    <div className="ni-page">
      <div className="ni-shell">
        <header className="ni-header">
          <div className="ni-brand">
            <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">Support Tools</span>
              <span className="ni-brand-title">Start an RMA</span>
              <span className="ni-brand-subtitle">
                {LOOPS.support.name} ({LOOPS.support.system}) → {LOOPS.service.name} ({LOOPS.service.system})
              </span>
            </div>
          </div>
          <div className="ni-toolbar">
            <button className="ni-button ni-button--ghost" onClick={() => navigate('/support')}>← Support Tools</button>
            <button className="ni-button ni-button--ghost" onClick={() => navigate('/support/flow')}>The flow</button>
          </div>
        </header>

        <section className="ni-panel">
          <p style={{ margin: '0 0 14px', fontSize: 14, lineHeight: 1.65, color: 'var(--ni-text-soft)' }}>
            The door from the support loop into the service loop. Paste the service tags you have in front of you —
            one per line, or separated by commas — and this resolves each one against the device registry, shows you
            whether it already has a live case, and opens the RMA form on the right device page.
          </p>
          <textarea
            className="ni-textarea"
            value={raw}
            onChange={e => { setRaw(e.target.value); if (error) setError(''); }}
            onKeyDown={onKeyDown}
            rows={4}
            placeholder={'C240036154\nC240036152\nC240035699'}
            style={{ width: '100%', maxWidth: 520, fontFamily: 'inherit' }}
            aria-label="Service tags"
          />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
            <button
              type="button"
              onClick={resolve}
              disabled={loading}
              className="ni-button ni-button--primary"
              style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Looking up…' : 'Look up tags'}
            </button>
            <span style={{ fontSize: 11.5, color: 'var(--ni-text-muted)' }}>
              Up to 25 tags. Ctrl/⌘ + Enter to run.
            </span>
          </div>
          {error && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#F09090' }}>{error}</p>}
        </section>

        {results && (
          <section className="ni-panel">
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 4 }}>
              <h2 className="ni-section-title" style={{ margin: 0 }}>
                {foundCount} of {results.length} resolved
              </h2>
            </div>
            {foundCount > 1 && (
              <div className="ni-callout" style={{ marginTop: 10, marginBottom: 14 }}>
                <strong>More than one unit?</strong> A case can hold several units. Start the RMA on the first unit and
                add the others on the form, or start them separately and bundle the cases together from the RMA dashboard.
                Either way the case cannot leave <code style={{ fontSize: 12 }}>repairing</code> until every unit is finished.
              </div>
            )}
            <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
              {results.map(r => <ResultCard key={r.tag} result={r} navigate={navigate} />)}
            </div>
          </section>
        )}

        <section className="ni-panel">
          <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>What happens when you submit</h2>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.75, color: 'var(--ni-text-soft)' }}>
            <li>The case is created at status <code style={{ fontSize: 12 }}>pending</code> with a new RMA ID.</li>
            <li>A matching Odoo Helpdesk ticket opens and its id is stored on the case, which is what keeps the support loop in sight of it.</li>
            <li>The customer gets the <code style={{ fontSize: 12 }}>created</code> email with the RMA ID.</li>
            <li>The MTTR clock starts here — not when the unit arrives — so triage and assignment time counts.</li>
            <li>Next step is yours: assign Haugesund or Copenhagen, which is what releases the return address to the customer.</li>
          </ol>
          <p className="ni-note" style={{ marginBottom: 0, marginTop: 12 }}>
            Full detail in <button
              type="button"
              onClick={() => navigate('/support/flow')}
              style={{ font: 'inherit', fontSize: 'inherit', color: 'var(--ni-green)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
            >Flow B — the service loop</button>.
          </p>
        </section>
      </div>
    </div>
  );
}

function ResultCard({ result, navigate }) {
  if (result.state !== 'found') {
    const isMissing = result.state === 'missing';
    return (
      <div style={{
        border: `1px solid ${isMissing ? 'rgba(217,180,74,0.5)' : 'rgba(224,96,96,0.5)'}`,
        background: isMissing ? 'rgba(217,180,74,0.06)' : 'rgba(224,96,96,0.06)',
        borderRadius: 14, padding: '12px 15px',
      }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <code style={{ fontSize: 13, fontWeight: 700, color: 'var(--ni-text)' }}>{result.tag}</code>
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.11em', textTransform: 'uppercase',
            color: isMissing ? '#E8CD7E' : '#F09090',
          }}>{isMissing ? 'no match' : 'lookup failed'}</span>
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-muted)', marginTop: 5 }}>
          {isMissing
            ? 'No device in the registry carries this service tag. Check for a transcription slip, or confirm the unit was actually commissioned through this system.'
            : result.message}
        </div>
      </div>
    );
  }

  const d = result.device;
  const status = getWarrantyStatus(d.scandate);
  const colour = getWarrantyColor(status);
  const openCase = (result.rmas || []).find(r => OPEN_STATUSES.has(String(r.status || '').toLowerCase()));
  const pastCount = (result.rmas || []).length - (openCase ? 1 : 0);

  return (
    <div style={{
      border: `1px solid ${openCase ? 'rgba(217,180,74,0.5)' : 'var(--ni-border-soft)'}`,
      background: openCase ? 'rgba(217,180,74,0.05)' : 'rgba(255,255,255,0.025)',
      borderRadius: 14, padding: '13px 16px',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
            <code style={{ fontSize: 13, fontWeight: 700, color: 'var(--ni-text)' }}>{d.serviceTagNumber}</code>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase', color: colour,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: colour }} />
              {status}
            </span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{d.productNumber || 'Unknown product'}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ni-text-muted)', marginTop: 3 }}>
            {d.serialNumber ? `Serial ${d.serialNumber}` : 'No serial recorded'}
            {d.scandate ? ` · scanned ${new Date(d.scandate).toLocaleDateString('en-CA')}` : ''}
            {pastCount > 0 ? ` · ${pastCount} previous RMA${pastCount === 1 ? '' : 's'}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="ni-button ni-button--ghost"
            style={{ fontSize: 12.5, minHeight: 38 }}
            onClick={() => navigate(`/device/${d._id}`)}
          >
            Device page
          </button>
          <button
            type="button"
            className={`ni-button ${openCase ? 'ni-button--muted' : 'ni-button--primary'}`}
            style={{ fontSize: 12.5, minHeight: 38 }}
            onClick={() => navigate(`/device/${d._id}?rma=1`)}
          >
            {openCase ? 'Start another anyway' : 'Start RMA →'}
          </button>
        </div>
      </div>

      {openCase && (
        <div style={{
          marginTop: 11, paddingTop: 10, borderTop: '1px solid rgba(217,180,74,0.35)',
          fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-soft)',
        }}>
          <strong style={{ color: '#E8CD7E' }}>Already has a live case · </strong>
          <code style={{ fontSize: 12 }}>{openCase.rmaId}</code> at{' '}
          <code style={{ fontSize: 12 }}>{openCase.status}</code>. Add this unit to that case or bundle them rather than
          opening a second one — duplicates split the history and distort the reporting the improvement loop runs on.
        </div>
      )}
    </div>
  );
}
